import { useState, useEffect } from 'react';
import StatsCard from '@/components/StatsCard';
import ViewButton from '@/components/ViewButton';
import CaseViewModal, { type CaseRow } from '@/components/CaseViewModal';
import { DataTable, SlideModal } from '@podus/ui';
import type { Column } from '@podus/ui';
import { LuWallet } from 'react-icons/lu';
import { api } from '@/lib/api';
import { exportCSV } from '@/lib/csv';
import { useYear } from '@/context/YearContext';
import type { Transaction, TransactionStats } from '@/types';

/* ─── Types ─── */

interface TransactionRow {
    id: number;
    caseId: number;
    caseNumber: string;
    sn: number;
    transactionId: string;
    dateTime: string;
    caseCategory: string;
    amountPaid: string;
    paymentMethod: string;
}

/* ─── Helpers ─── */

function mapToRow(t: Transaction & { caseCategory?: string }, index: number): TransactionRow {
    return {
        id: t.id,
        caseId: t.caseId,
        caseNumber: t.case?.caseNumber ?? '—',
        sn: index + 1,
        transactionId: t.transactionId,
        dateTime: new Date(t.createdAt).toLocaleString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        }),
        caseCategory: t.caseCategory ?? '—',
        amountPaid: `₦${Number(t.amountPaid).toLocaleString()}`,
        paymentMethod: t.paymentMethod,
    };
}

/* ─── Transaction View Modal ─── */

interface TransactionViewModalProps {
    open: boolean;
    onClose: () => void;
    transaction: TransactionRow | null;
    onViewCase: () => void;
}

function TransactionViewModal({ open, onClose, transaction, onViewCase }: TransactionViewModalProps) {
    if (!transaction) return null;

    return (
        <SlideModal open={open} onClose={onClose} title={`${transaction.caseCategory} Case`} maxWidth="max-w-lg">
            <div className="flex-1 overflow-y-auto px-6 pt-6">
                <div className="rounded-xl border border-[#eee] p-5">
                    <div className="mb-4 flex items-center gap-2 border-b border-[#eee] pb-3">
                        <LuWallet size={18} className="text-secondary" />
                        <h3 className="text-[16px] font-bold text-secondary">Financials</h3>
                    </div>

                    <div className="grid grid-cols-3 gap-y-5">
                        <div>
                            <p className="text-[13px] font-semibold text-secondary">Transaction ID</p>
                            <p className="mt-1 text-[14px] text-[#555]">{transaction.transactionId}</p>
                        </div>
                        <div>
                            <p className="text-[13px] font-semibold text-secondary">Date &amp; Time</p>
                            <p className="mt-1 text-[14px] text-[#555]">{transaction.dateTime}</p>
                        </div>
                        <div>
                            <p className="text-[13px] font-semibold text-secondary">Amount Paid</p>
                            <p className="mt-1 text-[14px] text-[#555]">{transaction.amountPaid}</p>
                        </div>
                        <div>
                            <p className="text-[13px] font-semibold text-secondary">Payment Method</p>
                            <p className="mt-1 text-[14px] text-[#555]">{transaction.paymentMethod}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-6 pb-6 pt-4">
                <button
                    type="button"
                    onClick={onViewCase}
                    className="flex w-full items-center justify-center rounded-xl bg-secondary py-4 text-[15px] font-bold text-white transition-all hover:bg-secondary-400"
                >
                    View Case Details
                </button>
            </div>
        </SlideModal>
    );
}

/* ─── Component ─── */

export default function Financials() {
    const { yearQuery } = useYear();

    const [transactions, setTransactions] = useState<TransactionRow[]>([]);
    const [stats, setStats] = useState<TransactionStats | null>(null);
    const [selectedTransaction, setSelectedTransaction] = useState<TransactionRow | null>(null);
    const [caseViewData, setCaseViewData] = useState<CaseRow | null>(null);

    useEffect(() => {
        api.get<(Transaction & { caseCategory?: string })[]>(
            `/api/transactions${yearQuery}&limit=100`,
        )
            .then((res) => setTransactions((res.response.data ?? []).map(mapToRow)))
            .catch(() => setTransactions([]));
    }, [yearQuery]);

    useEffect(() => {
        api.get<TransactionStats>(`/api/transactions/stats${yearQuery}`)
            .then((res) => setStats(res.response.data ?? null))
            .catch(() => setStats(null));
    }, [yearQuery]);

    const statsCards = [
        { label: 'Gross Revenue', value: `₦${Number(stats?.grossRevenue ?? 0).toLocaleString()}` },
    ];

    const COLUMNS: Column<TransactionRow>[] = [
        { key: 'sn', label: 'S/N' },
        { key: 'caseNumber', label: 'Case Number' },
        { key: 'transactionId', label: 'Transaction ID' },
        { key: 'dateTime', label: 'Date & Time' },
        { key: 'amountPaid', label: 'Amount Paid' },
        {
            key: 'actions',
            label: 'Actions',
            render: (row) => <ViewButton onClick={() => setSelectedTransaction(row)} />,
        },
    ];

    return (
        <div className="space-y-6  py-6">
            <div className="flex flex-col gap-4 px-4 lg:flex-row lg:items-start lg:justify-between lg:px-8">
                <h1 className="text-2xl font-bold text-secondary sm:text-3xl">Financials</h1>
                <div className="grid grid-cols-1 gap-3">
                    {statsCards.map((s) => (
                        <StatsCard key={s.label} stat={s} />
                    ))}
                </div>
            </div>

            <DataTable
                title="Transactions"
                columns={COLUMNS}
                data={transactions}
                getRowKey={(row) => row.id}
                onExportCSV={() => exportCSV('transactions', COLUMNS, transactions)}
                tabs={['All']}
                activeTab="All"
            />

            <TransactionViewModal
                open={!!selectedTransaction}
                onClose={() => setSelectedTransaction(null)}
                transaction={selectedTransaction}
                onViewCase={() => {
                    if (!selectedTransaction) return;
                    setCaseViewData({
                        id: selectedTransaction.caseId,
                        sn: 0,
                        date: selectedTransaction.dateTime,
                        complainantName: '—',
                        caseCategory: selectedTransaction.caseCategory,
                        disputeCategory: 'Civil',
                        amountPaid: selectedTransaction.amountPaid,
                        status: 'New',
                    });
                    setSelectedTransaction(null);
                }}
            />

            <CaseViewModal open={!!caseViewData} onClose={() => setCaseViewData(null)} caseData={caseViewData} />
        </div>
    );
}
